import React, { useEffect, useRef, useState } from 'react';
import { BookMarked, Folder, GitFork, HelpCircle, Layers, Truck, Users, X } from 'lucide-react';
import { tourScriptSeesRfqs, type ProjectFilesTourScript } from '../../utils/projectFilesTourSteps';
import './FilesHelpButton.css';

export interface FilesHelpButtonProps {
  script: ProjectFilesTourScript;
  /** Re-runs the Files walkthrough. */
  onReplay: () => void;
  /** Staff only: jump to Settings → Project Defaults → Designer Standards. */
  onOpenDesignerStandardsSettings?: () => void;
}

/**
 * Compact help affordance for the Files tab toolbar. Click opens a summary of
 * the four views; "Replay the walkthrough" re-runs the driver.js tour.
 */
const FilesHelpButton: React.FC<FilesHelpButtonProps> = ({ script, onReplay, onOpenDesignerStandardsSettings }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const showSubmittals = script === 'staff' || script === 'engineer';
  const showRfqs = tourScriptSeesRfqs(script);
  const showFolders = script !== 'supplier';

  return (
    <div className="pf-help" ref={rootRef}>
      <button
        type="button"
        className="pf-help-btn"
        data-tour="pf-help-btn"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-label="Files help"
        title="Files help"
      >
        <HelpCircle size={15} />
      </button>
      {open && (
        <div className="pf-help-card" role="dialog" aria-label="Files help">
          <div className="pf-help-card-head">
            <span className="pf-help-card-title">How the Files tab works</span>
            <button type="button" className="pf-help-card-close" onClick={() => setOpen(false)} aria-label="Close">
              <X size={14} />
            </button>
          </div>
          <ul className="pf-help-list">
            <li>
              <Layers size={14} />
              <span>
                <strong>Deliverables</strong> — file streams grouped by structure. Each stream moves through
                levels of detail at its own pace; the version history is the audit trail.
              </span>
            </li>
            {showSubmittals && (
              <>
                <li>
                  <GitFork size={14} />
                  <span>
                    <strong>Structures</strong> — unlock once the first timber list is in and the proposal lists
                    structures. The job can then split into structure groups; every track type (2D, 3D, HTML, timber
                    list) continues under each group and inherits the same versions — nothing is copied. The
                    whole-project originals become read-only roots and are no longer listed; their versions sit at
                    the base of every structure stream. Hover &ldquo;Deliverables&rdquo; for the split history.
                    After a job has split, admins and PMs can add another structure from the Deliverables heading —
                    as its own group, or into a group that already exists.
                  </span>
                </li>
                <li>
                  <Users size={14} />
                  <span>
                    <strong>Submittals</strong> — everything released to the client portal, grouped by level of
                    detail or by design phase (Concept / Engineering), split into 2D documentation and 3D files.
                    Chips show what is on the portal, awaiting approval, approved or replaced.
                  </span>
                </li>
              </>
            )}
            {showRfqs && (
              <li>
                <Truck size={14} />
                <span>
                  <strong>RFQs</strong> — admins, PMs and full employees only. Every file staged or sent to providers
                  (including proposal pushes), by structure scope and supplier, with where each supplier stands (Sent,
                  Quoted, Approved or Not bidding) and the quote amount. &ldquo;Whole project&rdquo; starts folded once
                  a job has structure scopes.
                </span>
              </li>
            )}
            {showFolders && (
              <li>
                <Folder size={14} />
                <span>
                  <strong>Project folders</strong> — Admin, Reference, Client Submittals and Designer Standards
                  on every project. Older uploads sit in Unsorted until you file them; it hides once empty.
                </span>
              </li>
            )}
            {showFolders && (
              <li>
                <BookMarked size={14} />
                <span>
                  <strong>Designer Standards</strong> — one shared library (SOPs, .2d / .3d templates, timber-list
                  export), identical on every project.
                  {onOpenDesignerStandardsSettings ? (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="pf-help-inline-link"
                        onClick={() => {
                          setOpen(false);
                          onOpenDesignerStandardsSettings();
                        }}
                      >
                        Manage in Settings
                      </button>
                    </>
                  ) : null}
                </span>
              </li>
            )}
          </ul>
          <button
            type="button"
            className="pf-help-replay"
            onClick={() => {
              setOpen(false);
              onReplay();
            }}
          >
            Replay the walkthrough
          </button>
        </div>
      )}
    </div>
  );
};

export default FilesHelpButton;
